/**
 * System kontroli dostępu oparty na rolach dla członków rodziny
 */

// import { getDB } from './indexedDb';
import { supabase } from '../lib/supabase';
import logger from './logger'

// Typy ról w systemie
export type FamilyRole = 'parent_a' | 'parent_b' | 'child_viewer';

// Interfejs uprawnień
export interface Permission {
  action: 'create' | 'read' | 'update' | 'delete' | 'invite' | 'manage';
  resource: 'family' | 'child' | 'calendar_entry' | 'member' | 'settings';
}

// Mapa uprawnień dla poszczególnych ról
const rolePermissions: Record<FamilyRole, Permission[]> = {
  'parent_a': [
    // Pełne uprawnienia do wszystkiego
    { action: 'create', resource: 'family' },
    { action: 'read', resource: 'family' },
    { action: 'update', resource: 'family' },
    { action: 'delete', resource: 'family' },
    { action: 'create', resource: 'child' },
    { action: 'read', resource: 'child' },
    { action: 'update', resource: 'child' },
    { action: 'delete', resource: 'child' },
    { action: 'create', resource: 'calendar_entry' },
    { action: 'read', resource: 'calendar_entry' },
    { action: 'update', resource: 'calendar_entry' },
    { action: 'delete', resource: 'calendar_entry' },
    { action: 'invite', resource: 'member' },
    { action: 'read', resource: 'member' },
    { action: 'update', resource: 'member' },
    { action: 'delete', resource: 'member' },
    { action: 'manage', resource: 'settings' },
  ],
  'parent_b': [
    // Rodzic - pełny dostęp do kalendarza, ograniczony do ustawień
    { action: 'read', resource: 'family' },
    { action: 'read', resource: 'child' },
    { action: 'create', resource: 'calendar_entry' },
    { action: 'read', resource: 'calendar_entry' },
    { action: 'update', resource: 'calendar_entry' },
    { action: 'delete', resource: 'calendar_entry' },
    { action: 'read', resource: 'member' },
  ],
  'child_viewer': [
    // Obserwator - tylko odczyt
    { action: 'read', resource: 'family' },
    { action: 'read', resource: 'child' },
    { action: 'read', resource: 'calendar_entry' },
    { action: 'read', resource: 'member' },
  ],
};

/**
 * Czyści cache roli użytkownika
 * @param userId ID użytkownika (opcjonalne - jeśli nie podano, czyści cały cache)
 */
export const clearUserRoleCache = (userId?: string) => {
  if (userId) {
    // Wyczyść cache dla konkretnego użytkownika
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith(`user-role-${userId}-`)) {
        sessionStorage.removeItem(key);
      }
    });
  } else {
    // Wyczyść cały cache roli użytkowników
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('user-role-')) {
        sessionStorage.removeItem(key);
      }
    });
  }
};

/**
 * Pobiera rolę użytkownika w rodzinie
 * @param userId ID użytkownika
 * @param familyId ID rodziny
 * @returns Rola użytkownika lub null, jeśli nie jest członkiem rodziny
 */
export const getUserRole = async (userId: string, familyId: string): Promise<FamilyRole | null> => {
  try {
    // Sprawdź cache
    const cacheKey = `user-role-${userId}-${familyId}`
    const cached = sessionStorage.getItem(cacheKey)
    if (cached) {
      try {
        const parsed = JSON.parse(cached)
        // Cache ważny przez 5 minut
        if (Date.now() - parsed.timestamp < 5 * 60 * 1000) {
  // Zredukuj głośność logów – zostaw infę tylko w trybie debug
  // logger.debug('📦 Używam cache dla roli użytkownika')
          return parsed.role
        }
      } catch (e) {
        // Ignoruj błędy parsowania cache
      }
    }

    // W rzeczywistej implementacji moglibyśmy używać IndexedDB dla szybszego dostępu offline
    // Jednak obecnie IndexedDB nie przechowuje członków rodziny, więc używamy tylko Supabase
    // const db = await getDB();
    // const members = await db.getAllFromIndex('family_members', 'by-user', userId);
    // 
    // const member = members.find(m => m.family_id === familyId);
    // if (member) {
    //   return member.role as FamilyRole;
    // }
    
    // Jeśli nie znaleziono lokalnie, sprawdź w Supabase bez sztucznego timeoutu
    const queryPromise = supabase
      .from('family_members')
      .select('role')
      .eq('user_id', userId)
      .eq('family_id', familyId)
      .single();
    
    const { data, error } = await queryPromise as any;
    
    if (error) {
      if (error.code === 'PGRST116') {
        // PGRST116 to "Did not find a single row" - użytkownik nie należy do żadnej rodziny
        // Cache null przez krótszy czas (1 minuta)
        sessionStorage.setItem(cacheKey, JSON.stringify({
          role: null,
          timestamp: Date.now()
        }))
        return null;
      }
      throw error;
    }
    
    const role = data?.role as FamilyRole || null;
    
    // Cache wynik
    sessionStorage.setItem(cacheKey, JSON.stringify({
      role,
      timestamp: Date.now()
    }));
    
    return role;
  } catch (error: any) {
    logger.error('❌ Błąd pobierania roli użytkownika:', error);
    // W przypadku błędu zwracamy null zamiast rzucania wyjątku
    return null;
  }
};

/**
 * Sprawdza, czy użytkownik ma określone uprawnienie
 * @param userId ID użytkownika
 * @param familyId ID rodziny
 * @param permission Uprawnienie do sprawdzenia
 * @returns true, jeśli użytkownik ma uprawnienie, false w przeciwnym razie
 */
export const hasPermission = async (
  userId: string,
  familyId: string,
  permission: Permission
): Promise<boolean> => {
  try {
    const role = await getUserRole(userId, familyId);
    if (!role) {
      return false;
    }
    
    const permissions = rolePermissions[role];
    return permissions.some(p => 
      p.action === permission.action && p.resource === permission.resource
    );
  } catch (error) {
    logger.error('❌ Błąd sprawdzania uprawnień:', error);
    return false;
  }
};

/**
 * Sprawdza, czy użytkownik ma rolę o określonym poziomie lub wyższym
 * @param userId ID użytkownika
 * @param familyId ID rodziny
 * @param minimumRole Minimalna wymagana rola
 * @returns true, jeśli użytkownik ma wymaganą rolę lub wyższą, false w przeciwnym razie
 */
export const hasMinimumRole = async (
  userId: string,
  familyId: string,
  minimumRole: FamilyRole
): Promise<boolean> => {
  const role = await getUserRole(userId, familyId);
  if (!role) {
    return false;
  }
  
  const roleHierarchy: FamilyRole[] = ['child_viewer', 'parent_b', 'parent_a'];
  const userRoleIndex = roleHierarchy.indexOf(role);
  const minimumRoleIndex = roleHierarchy.indexOf(minimumRole);
  
  return userRoleIndex >= minimumRoleIndex;
};

/**
 * Aktualizuje rolę użytkownika w rodzinie
 * @param targetUserId ID użytkownika, którego rola ma być zaktualizowana
 * @param familyId ID rodziny
 * @param newRole Nowa rola
 * @param currentUserId ID użytkownika dokonującego zmiany (musi mieć uprawnienia)
 * @returns true, jeśli aktualizacja się powiodła, false w przeciwnym razie
 */
export const updateUserRole = async (
  targetUserId: string,
  familyId: string,
  newRole: FamilyRole,
  currentUserId: string
): Promise<boolean> => {
  try {
    // Sprawdź, czy bieżący użytkownik ma uprawnienia do aktualizacji członków
    const hasUpdatePermission = await hasPermission(
      currentUserId,
      familyId,
      { action: 'update', resource: 'member' }
    );
    
    if (!hasUpdatePermission) {
      logger.error('❌ Brak uprawnień do aktualizacji roli użytkownika');
      return false;
    }
    
    // TWARDY WARUNEK: nie więcej niż 2 rodziców (parent_a + parent_b)
    if (newRole === 'parent_b') {
      const { data: currentMembers, error: membersError } = await supabase
        .from('family_members')
        .select('user_id, role')
        .eq('family_id', familyId)

      if (membersError) {
        logger.error('❌ Błąd pobierania członków rodziny przed zmianą roli:', membersError)
        throw membersError
      }

      const parentCount = (currentMembers || []).filter(m => m.role === 'parent_a' || m.role === 'parent_b').length
      const targetIsAlreadyParent = (currentMembers || []).some(m => m.user_id === targetUserId && (m.role === 'parent_a' || m.role === 'parent_b'))
      if (parentCount >= 2 && !targetIsAlreadyParent) {
        throw new Error('Maksymalnie można mieć 2 rodziców w rodzinie (parent_a + parent_b)')
      }
    }

    // Aktualizuj rolę w Supabase przez funkcję RPC (bezpieczniej)
    const { data, error } = await supabase.rpc('update_family_member_role', {
      p_target_user_id: targetUserId,
      p_family_id: familyId,
      p_new_role: newRole,
      p_current_user_id: currentUserId
    });
    
    if (error) {
      logger.error('❌ Błąd RPC update_family_member_role:', error);
      throw error;
    }
    
    if (!data) {
      throw new Error('Nie udało się zaktualizować roli');
    }
    
    // Wyczyść cache ról po aktualizacji
    logger.debug('🗑️ Czyszczenie cache po aktualizacji roli...');
    
    // Wyczyść cache roli dla zaktualizowanego użytkownika
    clearUserRoleCache(targetUserId);
    
    // Wyczyść cache członków rodziny
    const familyMembersKey = `family-members-${familyId}`;
    sessionStorage.removeItem(familyMembersKey);
    logger.debug(`🗑️ Wyczyszczono cache członków rodziny: ${familyMembersKey}`);
    
    // W rzeczywistej implementacji moglibyśmy aktualizować również lokalnie w IndexedDB
    // Jednak obecnie IndexedDB nie przechowuje członków rodziny, więc używamy tylko Supabase
    // const db = await getDB();
    // const members = await db.getAllFromIndex('family_members', 'by-user', targetUserId);
    // const member = members.find(m => m.family_id === familyId);
    // 
    // if (member) {
    //   member.role = newRole;
    //   await db.put('family_members', member);
    // }
    
    return true;
  } catch (error) {
    logger.error('❌ Błąd aktualizacji roli użytkownika:', error);
    return false;
  }
};

/**
 * Hook do sprawdzania, czy komponent powinien być renderowany na podstawie uprawnień
 * @param userId ID użytkownika
 * @param familyId ID rodziny
 * @param permission Wymagane uprawnienie
 * @returns true, jeśli komponent powinien być renderowany, false w przeciwnym razie
 */
export const canRender = async (
  userId: string,
  familyId: string,
  permission: Permission
): Promise<boolean> => {
  return await hasPermission(userId, familyId, permission);
};

/**
 * Sprawdza dostęp do tabów ustawień na podstawie roli
 */
export const getSettingsTabAccess = (role: FamilyRole | null) => {
  if (!role) {
    return {
      family: false,
      members: false,
      install: true,
      security: true, // Każdy może zmienić swoje hasło
      appearance: true, // Każdy może wybrać swój schemat
      history: true // Każdy może przeglądać historię
    };
  }

  switch (role) {
    case 'parent_a': // Administrator - pełny dostęp
      return {
        family: true,     // może edytować
        members: true,    // może edytować
        install: true,
        security: true,   // może zmienić hasło
        appearance: true, // może zmienić wygląd
        history: true // może przeglądać historię
      };
      
    case 'parent_b': // Drugi rodzic - ograniczony dostęp
      return {
        family: true,     // tylko podgląd
        members: true,    // może oglądać, ale nie edytować
        install: true,
        security: true,   // może zmienić hasło
        appearance: true, // może zmienić wygląd
        history: true // może przeglądać historię
      };
      
    case 'child_viewer': // Obserwator - bardzo ograniczony dostęp
      return {
        family: false,    // nie widzi zakładki
        members: false,   // nie widzi zakładki
        install: true,
        security: true,   // może zmienić hasło
        appearance: true, // może zmienić wygląd
        history: false    // nie widzi historii
      };
      
    default:
      return {
        family: false,
        members: false,
        install: true,
        security: true,
        appearance: true,
        history: true
      };
  }
};

/**
 * Sprawdza czy użytkownik może edytować kalendarz
 */
export const canEditCalendar = (role: FamilyRole | null): boolean => {
  if (!role) return false;
  
  // Tylko parent_a i parent_b mogą edytować kalendarz
  return role === 'parent_a' || role === 'parent_b';
};